import { useState, Fragment } from 'react';
import styles from './StudentProgressRow.module.scss';
import ModalContainer from '../components/ModalContainer';
import StudentProgressModal from './StudentProgressModal';
import { ActivityInfo, ComprehensionDataPoint, StruggleDataPoint } from '../../peekapak-types/PeekavilleGameDataTypes';

interface Props {
  studentName: string;
  isShowStudents: boolean;
  unitData?: ActivityInfo[];
  isFirstRow?: boolean;
}

interface ModalContent {
  title: string;
  level: number;
  struggleData: StruggleDataPoint[] | ComprehensionDataPoint[];
}

const StudentProgressRow = ({ studentName, isShowStudents, unitData, isFirstRow }: Props): JSX.Element => {
  const [isShowModal, setShowModal] = useState(false);
  const [modalContent, setModalContent] = useState<ModalContent>({
    title: '',
    level: 0,
    struggleData: [],
  });
  const hideModal = () => setShowModal(false);
  const getBackgroundColor = (colorPercent: number) => {
    if (colorPercent === 0) return '#a5a5a5';
    if (colorPercent < 34) return '#d60000';
    if (colorPercent < 68) return '#ffd100';
    if (colorPercent >= 68) return '#69be28';
  };

  const populateModal = (level: ActivityInfo, struggleData: StruggleDataPoint[] | ComprehensionDataPoint[]) => {
    if (struggleData.length === 0) return;
    setModalContent({
      title: level.displayName,
      level: struggleData[0].levelPlayed ? struggleData[0].levelPlayed : getReadingLevelDisplay(level.readingLevel),
      struggleData: struggleData,
    });
    setShowModal(true);
  };

  const getPercentComplete = (levelData: ActivityInfo) => {
    const { lastDateOpened = 0, completionPercent = '0', struggleData = [], levels, type } = levelData;
    if (type !== 'book' && levels && levels === 1) return parseInt(completionPercent);
    if (struggleData.length === 0 && parseInt(completionPercent) === 0) return 0;
    const mostRecentStruggle = struggleData.length > 0 && struggleData.sort((a, b) => b.createdAt - a.createdAt)[0];
    if (mostRecentStruggle && type === 'book') {
      const completion = Math.max(
        1,
        Math.ceil((mostRecentStruggle.numRight / (mostRecentStruggle.numWrong + mostRecentStruggle.numRight)) * 100),
      );
      return completion;
    }
    if (lastDateOpened === 0 || lastDateOpened === '0') return 0;
    if (mostRecentStruggle && type !== 'book') {
      return mostRecentStruggle.rightNum === 0 && mostRecentStruggle.wrongNum === 0
        ? parseInt(completionPercent)
        : Math.max(
            1,
            Math.ceil(
              (mostRecentStruggle.rightNum / (mostRecentStruggle.rightNum + mostRecentStruggle.wrongNum)) * 100,
            ),
          );
    }
    return 0;
  };
  const getReadingLevelDisplay = (readingLevel: string) => {
    const mapping: { [key: string]: string } = {
      '-1': 'Pre-K to K',
      '0': 'Pre-K to K',
      '1': 'K to Gr 1',
      '2': 'Gr 1 to Gr 2',
      '3': 'Gr 3',
      '4': 'Gr 4 to Gr 5',
    };
    return mapping[readingLevel];
  };

  const getActivityContent = (activity: ActivityInfo) => {
    const { levels, id, struggleData = [], completionPercent = '0' } = activity;
    const levelsToDisplay = parseInt(completionPercent) > 0 && !struggleData ? 1 : levels;
    const activityLevels = new Array(levelsToDisplay).fill(null);

    const levelData = activityLevels.map((_, index) => {
      if (activity.type === 'book') return activity;
      const thisLevelStruggleData = struggleData.some((item) => item.levelPlayed === 0)
        ? struggleData.filter((item: StruggleDataPoint) => item.levelPlayed === index)
        : struggleData.filter((item: StruggleDataPoint) => item.levelPlayed - 1 === index);
      if (thisLevelStruggleData) return { ...activity, struggleData: thisLevelStruggleData };
      return activity;
    });
    return (
      <div className={styles.levelOrganizer}>
        {levelData.map((level, index) => {
          const colorPercent = getPercentComplete(level) || 0;
          return (
            <Fragment key={`${studentName}${id}${index}`}>
              <div
                className={styles.completionSquare}
                style={{
                  backgroundColor: getBackgroundColor(colorPercent),
                  outline:
                    (level.type === 'book' && level.struggleData && level.struggleData.length === 0) ||
                    (level.type === 'book' && level.timeSpent && parseInt(level.timeSpent) > 0)
                      ? '3px solid #69be28'
                      : 'none',
                  cursor: level.struggleData && level.struggleData.length > 0 ? 'pointer' : 'cursor',
                }}
                onClick={() => populateModal(level, level.struggleData || [])}
              >
                {isFirstRow && levels > 1 && `L${index + 1}`}
                {level.type === 'book' &&
                  level.readingLevel &&
                  `Level: ${getReadingLevelDisplay(level.readingLevel.toString())}`}
              </div>
              {level.struggleData && level.struggleData.length > 0 && isShowModal && (
                <ModalContainer hideModal={hideModal}>
                  <StudentProgressModal
                    hideModal={hideModal}
                    struggleData={modalContent.struggleData}
                    level={modalContent.level}
                    title={modalContent.title}
                  />
                </ModalContainer>
              )}
            </Fragment>
          );
        })}
      </div>
    );
  };
  return (
    <div className={styles.container}>
      <div className={styles.nameCell}>
        <h4>{isShowStudents ? studentName : 'Private'}</h4>
      </div>
      {unitData ? (
        <div className={styles.unitDataContainer}>
          {unitData.slice(0, 4).map((activity, index) => (
            <div key={`${studentName}${index} activity.id`} className={styles.dataCell}>
              <div className={styles.smallScreenTitles}>{activity.displayName}</div>
              {getActivityContent(activity)}
            </div>
          ))}
        </div>
      ) : (
        <div className={styles.noData} key={`${studentName} noData`}>
          <p>
            {`This student doesn't have any myPeekaville data. Have they played
            the game?`}
          </p>
        </div>
      )}
    </div>
  );
};

export default StudentProgressRow;
